/**
 * URL slugs for values that are only ever strings in the database — today the machine
 * manufacturer, which is not an entity of its own.
 *
 * The same function exists in server/slug.js: the deployed image ships server/ without src/
 * (see the Dockerfile), so the SSR server cannot import this file. src/__tests__/slug.spec.js
 * asserts the two implementations are character-for-character identical — change one, change
 * the other.
 */

/**
 * Lowercase, ASCII-only, dash-separated slug.
 *
 * @param {string} value e.g. "Holz-Her Nord"
 * @returns {string} e.g. "holz-her-nord"
 */
export function slugify(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * The URL slug of a category: `url_slug` from the database, or the transliterated primary key
 * when the API hasn't got the column yet.
 *
 * The primary key is what `machines.category_slug` and `category_translations` point at, and it
 * is full of underscores and å/ä/ö ("cnc_dörrmaskiner"); the URL slug is the ASCII, hyphenated
 * name the landing page lives at. The fallback matters in one real case: the SSR server pointed
 * at a production API that is running an older schema — it keeps serving working links instead
 * of building `/sv/catalogue/undefined`.
 *
 * @param {{ urlSlug?: string, slug?: string }} category
 * @returns {string}
 */
export function categoryUrlSlug(category) {
  return category?.urlSlug || slugify(category?.slug);
}

/**
 * The same thing for a machine, which carries its category's slugs inline rather than the
 * category object.
 *
 * @param {{ categoryUrlSlug?: string, categorySlug?: string }} machine
 * @returns {string}
 */
export function machineCategoryUrlSlug(machine) {
  return machine?.categoryUrlSlug || slugify(machine?.categorySlug);
}
